import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 10000;

const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const DispatcherChat = ({ load, onClose, fetchWithAuth, user }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const driverName = load.assigned_driver || 'Driver';
  const dispatcherName = user?.full_name || user?.name || 'Dispatcher';
  const loadRef = load.order_number || load.id?.substring(0, 8).toUpperCase();

  const fetchMessages = async () => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/driver-mobile/loads/${load.id}/messages`);
      if (res.ok) {
        const data = await res.json();
        setMessages(Array.isArray(data) ? data : (data.messages || []));
      }
    } catch (err) {
      console.error('DispatcherChat: fetch error', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = newMessage.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/driver-mobile/loads/${load.id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: text, sender_type: 'dispatcher' }),
      });
      if (res.ok) {
        setNewMessage('');
        await fetchMessages();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send message');
      }
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg flex flex-col p-0 gap-0" style={{ height: '600px' }}>

        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-3 text-base font-semibold">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold flex-shrink-0">
              {driverName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate">{driverName}</div>
              <div className="text-xs text-muted-foreground font-normal">Load #{loadRef}</div>
            </div>
            <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-xs text-muted-foreground font-normal">Live</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
              <i className="fas fa-comments text-4xl mb-3 opacity-30"></i>
              <p className="text-sm font-medium">No messages yet</p>
              <p className="text-xs mt-1">Send a message to start the conversation</p>
            </div>
          ) : (
            messages.map((msg, idx) => {
              const isDispatcher = msg.sender_type === 'dispatcher';
              const prevMsg = messages[idx - 1];
              const showDateSep = !prevMsg || formatDate(msg.sent_at) !== formatDate(prevMsg.sent_at);

              return (
                <React.Fragment key={msg.id || idx}>
                  {showDateSep && (
                    <div className="flex items-center gap-2 py-1">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground px-1">{formatDate(msg.sent_at)}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  )}
                  <div className={`flex ${isDispatcher ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex flex-col gap-0.5 max-w-[72%] ${isDispatcher ? 'items-end' : 'items-start'}`}>
                      <span className={`text-xs text-muted-foreground ${isDispatcher ? 'mr-1' : 'ml-1'}`}>
                        {isDispatcher ? dispatcherName : driverName}
                      </span>
                      <div className={`px-3 py-2 text-sm leading-relaxed ${
                        isDispatcher
                          ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-sm'
                          : 'bg-muted text-foreground rounded-2xl rounded-bl-sm'
                      }`}>
                        {msg.content}
                      </div>
                      <span className={`text-xs text-muted-foreground ${isDispatcher ? 'mr-1' : 'ml-1'}`}>
                        {formatTime(msg.sent_at)}
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t flex-shrink-0 flex gap-2 items-center">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${driverName}…`}
            className="flex-1"
            disabled={sending}
            autoFocus
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="flex-shrink-0 w-10 h-10 p-0"
          >
            {sending
              ? <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              : <i className="fas fa-paper-plane text-sm"></i>
            }
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default DispatcherChat;
